import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import {
  wooGetOrdersInRange,
  wooGetProducts,
  WooOrder,
  WooOrderStatus,
  WooProduct,
} from "../../services/woo";

function formatCurrency(v: number, currency = "USD") {
  try {
    return v.toLocaleString("en-US", { style: "currency", currency });
  } catch {
    return `$${v.toFixed(2)}`;
  }
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US");
}

/* Small helper */
function StatusBadge({
  status,
}: {
  status: "paid" | "pending" | "refunded" | "cancelled";
}) {
  const map: Record<"paid" | "pending" | "refunded" | "cancelled", string> = {
    paid: "success",
    pending: "warning",
    refunded: "danger",
    cancelled: "secondary",
  };
  return (
    <span className={`badge bg-${map[status]} text-capitalize text-white`}>
      {status}
    </span>
  );
}

function mapWooStatus(
  status: WooOrderStatus
): "paid" | "pending" | "refunded" | "cancelled" {
  switch (status) {
    case "completed":
    case "processing":
      return "paid";
    case "refunded":
      return "refunded";
    case "cancelled":
    case "failed":
    case "trash":
      return "cancelled";
    case "pending":
    case "on-hold":
    default:
      return "pending";
  }
}

export default function Dashboard() {
  const [orders, setOrders] = useState<WooOrder[]>([]);
  const [products, setProducts] = useState<WooProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load last 12 weeks of orders (≈ 84 days) + a page of products
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const now = new Date();
        const before = now.toISOString();
        const afterDate = new Date(now);
        afterDate.setDate(afterDate.getDate() - 84);
        const after = afterDate.toISOString();

        const [orders84d, productsPage] = await Promise.all([
          wooGetOrdersInRange(after, before, 100, 10),
          (async () => {
            const { products } = await wooGetProducts(1, 100);
            return products;
          })(),
        ]);

        if (!alive) return;
        setOrders(orders84d);
        setProducts(productsPage);
        setError(null);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load dashboard data");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Derived: recent orders table (latest 10)
  const recentOrders = useMemo(() => {
    const sorted = [...orders].sort(
      (a, b) =>
        new Date(b.date_created).getTime() - new Date(a.date_created).getTime()
    );
    return sorted.slice(0, 10).map((o) => {
      const fullName = `${o.billing?.first_name || ""} ${
        o.billing?.last_name || ""
      }`.trim();
      const customer = fullName || o.billing?.email || "Guest";
      return {
        id: o.number || String(o.id),
        date: o.date_created,
        customer,
        status: mapWooStatus(o.status),
        total: parseFloat(o.total || "0"),
        currency: o.currency || "USD",
      };
    });
  }, [orders]);

  // Derived: KPIs for last 30 days
  const kpis = useMemo(() => {
    const ordersToConsider = orders.filter((o) => {
      //just paid and processing orders, ignore pending, refunded, cancelled etc
      return o.status === "completed" || o.status === "processing";
    });
    const currency = ordersToConsider[0]?.currency || "USD";
    const since = new Date();
    since.setDate(since.getDate() - 30);

    const last30 = ordersToConsider.filter(
      (o) => new Date(o.date_created).getTime() >= since.getTime(),
    );

    const totalRevenue = last30.reduce(
      (sum, o) => sum + parseFloat(o.total || "0"),
      0
    );
    const totalOrders = last30.length;
    const avgOrder = totalOrders ? totalRevenue / totalOrders : 0;

    const customers = new Set(
      last30.map((o) => {
        const fullName = `${o.billing?.first_name || ""} ${
          o.billing?.last_name || ""
        }`.trim();
        return fullName || o.billing?.email || `guest-${o.id}`;
      })
    ).size;

    const refunded = last30.filter((o) => o.status === "refunded").length;
    const refundRate = totalOrders ? (refunded / totalOrders) * 100 : 0;

    // Keep the same mock formula label for now
    const conversion = ((totalOrders / 1000) * 100).toFixed(2);

    return {
      currency,
      totalRevenue,
      totalOrders,
      avgOrder,
      customers,
      conversion,
      refundRate,
      repeat: 36, // unknown from raw data; placeholder metric
    };
  }, [orders]);

  // Derived: sales by week for last 12 weeks
  const salesLast12Weeks = useMemo(() => {
    // Build 12 buckets (oldest -> newest) keyed by Monday of each week (UTC)
    function startOfWeekUTC(d: Date) {
      const dd = new Date(
        Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
      );
      const day = dd.getUTCDay(); // 0..6 (Sun..Sat)
      const diff = (day + 6) % 7; // days since Monday
      dd.setUTCDate(dd.getUTCDate() - diff);
      dd.setUTCHours(0, 0, 0, 0);
      return dd;
    }

    const now = new Date();
    const weeks: {
      key: string;
      date: Date;
      revenue: number;
      orders: number;
    }[] = [];

    let cursor = startOfWeekUTC(now);
    for (let i = 0; i < 12; i++) {
      weeks.unshift({
        key: cursor.toISOString(),
        date: new Date(cursor),
        revenue: 0,
        orders: 0,
      });
      cursor = new Date(cursor);
      cursor.setUTCDate(cursor.getUTCDate() - 7);
    }

    const bucketByKey = new Map(weeks.map((w) => [w.key, w] as const));

    orders.forEach((o) => {
      if (o.status !== "completed" && o.status !== "processing") return; // only count paid orders
      const d = new Date(o.date_created);
      const sow = startOfWeekUTC(d).toISOString();
      const bucket = bucketByKey.get(sow);
      if (bucket) {
        bucket.revenue += parseFloat(o.total || "0");
        bucket.orders += 1;
      }
    });

    // Map to chart-ready
    return weeks.map((w) => ({
      date: w.date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      revenue: Number(w.revenue.toFixed(2)),
      orders: w.orders,
    }));
  }, [orders]);

  // Derived: products lists
  const topProducts = useMemo(() => {
    const withSales = products.map((p) => ({
      id: p.id,
      name: p.name,
      category: p.categories?.[0]?.name || "-",
      totalSold: Number((p as any).total_sales || 0),
      stock: p.manage_stock ? p.stock_quantity ?? 0 : null,
    }));
    return withSales.sort((a, b) => b.totalSold - a.totalSold).slice(0, 5);
  }, [products]);

  const lowStock = useMemo(() => {
    const list = products
      .filter((p) => p.manage_stock && typeof p.stock_quantity === "number")
      .map((p) => ({
        id: p.id,
        name: p.name,
        category: p.categories?.[0]?.name || "-",
        stock: p.stock_quantity as number,
      }))
      .filter((p) => p.stock <= 10)
      .sort((a, b) => a.stock - b.stock);
    return list;
  }, [products]);

  const maxRevenue = Math.max(...salesLast12Weeks.map((s) => s.revenue), 1);

  return (
    <div className="page-layout">
      <div className="page-header">
        <h2 className="title">Dashboard</h2>
        <div className="right"></div>
      </div>

      <div className="section-card">
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        {/* KPIs */}
        <div className="row g-3">
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Revenue (30d)</div>
                <div className="h4 mb-0">
                  {loading
                    ? "…"
                    : formatCurrency(kpis.totalRevenue, kpis.currency)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Orders (30d)</div>
                <div className="h4 mb-0">
                  {loading ? "…" : kpis.totalOrders}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Avg. Order Value</div>
                <div className="h4 mb-0">
                  {loading ? "…" : formatCurrency(kpis.avgOrder, kpis.currency)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Customers (30d)</div>
                <div className="h4 mb-0">{loading ? "…" : kpis.customers}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Sales (Last 12 Weeks) */}
        <div className="card my-3">
          <div className="card-body">
            <h5 className="card-title mb-3">Sales (Last 12 Weeks)</h5>

            <div style={{ width: "100", height: 260 }}>
              <ResponsiveContainer>
                <BarChart
                  data={salesLast12Weeks}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} domain={[0, maxRevenue]} />
                  <Tooltip
                    formatter={(value: number, name) =>
                      name === "revenue"
                        ? [
                            value.toLocaleString("en-US", {
                              style: "currency",
                              currency: orders[0]?.currency || "USD",
                            }),
                            "Revenue",
                          ]
                        : [value, "Orders"]
                    }
                  />
                  <Bar
                    dataKey="revenue"
                    fill="var(--bs-primary)"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="text-muted small mt-2">
              Tip: hover bars to see revenue and orders.
            </div>
          </div>
        </div>

        <div className="row g-3">
          {/* Recent Orders */}
          <div className="col-12 col-lg-7">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-3">Recent Orders</h5>
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Date</th>
                        <th scope="col">Customer</th>
                        <th scope="col">Status</th>
                        <th scope="col" className="text-end">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-4"
                          >
                            Loading…
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        recentOrders.map((o) => (
                          <tr key={o.id}>
                            <td>{o.id}</td>
                            <td>{formatDate(o.date)}</td>
                            <td>{o.customer}</td>
                            <td>
                              <StatusBadge status={o.status} />
                            </td>
                            <td className="text-end">
                              {formatCurrency(o.total, o.currency)}
                            </td>
                          </tr>
                        ))}
                      {!loading && recentOrders.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-4"
                          >
                            No recent orders.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Products: Top Sellers + Low Stock */}
          <div className="col-12 col-lg-5">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-3">Products</h5>
                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="text-muted">Top Sellers</h6>
                    <ul className="list-group">
                      {loading && (
                        <li className="list-group-item text-muted">Loading…</li>
                      )}
                      {!loading &&
                        topProducts.map((p) => (
                          <li
                            key={p.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <div className="fw-semibold">{p.name}</div>
                              <div className="text-muted small">
                                {p.category}
                              </div>
                            </div>
                            <span className="badge bg-primary rounded-pill">
                              {p.totalSold} sold
                            </span>
                          </li>
                        ))}
                    </ul>
                  </div>

                  <div className="col-12">
                    <h6 className="text-muted mt-2">Low Stock</h6>
                    {loading ? (
                      <div className="alert alert-light py-2 mb-0">
                        Loading…
                      </div>
                    ) : lowStock.length === 0 ? (
                      <div className="alert alert-success py-2 mb-0">
                        All good ✅
                      </div>
                    ) : (
                      <ul className="list-group">
                        {lowStock.map((p) => (
                          <li
                            key={p.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <div className="fw-semibold">{p.name}</div>
                              <div className="text-muted small">
                                {p.category}
                              </div>
                            </div>
                            <span
                              className={`badge rounded-pill ${
                                p.stock === 0
                                  ? "bg-danger"
                                  : "bg-warning text-dark"
                              }`}
                            >
                              {p.stock === 0
                                ? "Out of stock"
                                : `${p.stock} left`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* End content */}
      </div>
    </div>
  );
}
